import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import logger from '../utils/logger';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── helpers ────────────────────────────────────────────────────────────────
  const getToken = () => localStorage.getItem('authToken');

  const authAxios = () =>
    axios.create({
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
      },
    });

  const saveTokens = (token, refresh) => {
    localStorage.setItem('authToken', token);
    if (refresh) localStorage.setItem('refreshToken', refresh);
  };

  const clearTokens = () => {
    ['authToken', 'refreshToken', 'userData', 'userType', 'userId'].forEach(k =>
      localStorage.removeItem(k)
    );
  };

  // ─── fetch profile after login ───────────────────────────────────────────────
  const fetchUser = async () => {
    try {
      const res = await authAxios().get('accounts/user/');
      const userData = res.data;
      setUser(userData);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('userType', userData.user_type);
      return userData;
    } catch (err) {
      logger.error('fetchUser error:', err);
      return null;
    }
  };

  // ─── init on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }

    const cached = localStorage.getItem('userData');
    if (cached) {
      setUser(JSON.parse(cached));
      setLoading(false);
    } else {
      fetchUser().finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line

  // ─── register ───────────────────────────────────────────────────────────────
  // POST /api/accounts/register/
  // Body: { username, email, password, password2, first_name, last_name, phone_number, referral_code }
  // Returns: { message, phone_number, next_step: 'verify_phone' }
  const register = async (data) => {
    try {
      const res = await axios.post('accounts/register/', data);
      return { success: true, data: res.data, phone_number: res.data.phone_number };
    } catch (err) {
      const errors = err.response?.data;
      // Return first field error message for display
      const message = errors
        ? (typeof errors === 'object'
            ? Object.values(errors).flat().join(' ')
            : errors)
        : 'Registration failed. Please try again.';
      return { success: false, message };
    }
  };

  // ─── verify phone OTP ────────────────────────────────────────────────────────
  // POST /api/accounts/verify-phone/
  // Body: { phone_number, otp }
  // Returns: { message, access, refresh, user }
  const verifyPhone = async (phone_number, otp) => {
    try {
      const res = await axios.post('accounts/verify-phone/', { phone_number, otp });
      const { access, refresh, user: userData } = res.data;
      saveTokens(access, refresh);
      setUser(userData);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('userType', userData.user_type);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Invalid OTP. Please try again.',
      };
    }
  };

  // ─── resend OTP ──────────────────────────────────────────────────────────────
  // POST /api/accounts/resend-otp/
  // Body: { phone_number }
  const resendOTP = async (phone_number) => {
    try {
      const res = await axios.post('accounts/resend-otp/', { phone_number });
      return { success: true, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Failed to resend OTP.',
      };
    }
  };

  // ─── login ───────────────────────────────────────────────────────────────────
  // POST /api/accounts/login/
  // Body: { phone_number, password }
  // Returns: { token, refresh, user_id, user_type, is_verified, session_id, ... }
  const login = async (phone_number, password) => {
    try {
      const res = await axios.post('accounts/login/', { phone_number, password });
      const { token, refresh, user_id, user_type, session_id } = res.data;

      saveTokens(token, refresh);
      if (user_id)    localStorage.setItem('userId', String(user_id));
      if (user_type)  localStorage.setItem('userType', user_type);
      if (session_id) localStorage.setItem('sessionId', session_id);

      const userData = await fetchUser();
      if (!userData) return { success: false, message: 'Login succeeded but failed to load profile.' };

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Invalid credentials. Please check phone number and password.',
      };
    }
  };

  // ─── logout ──────────────────────────────────────────────────────────────────
  // POST /api/accounts/logout/
  // Body: { refresh, session_id }
  const logout = async () => {
    try {
      const refresh    = localStorage.getItem('refreshToken');
      const session_id = localStorage.getItem('sessionId');
      if (refresh) await authAxios().post('accounts/logout/', { refresh, session_id });
    } catch (_) { /* ignore */ }
    clearTokens();
    setUser(null);
  };

  // ─── password reset request ──────────────────────────────────────────────────
  // POST /api/accounts/v1/password-reset/request/
  // Body: { phone_number }
  const requestPasswordReset = async (phone_number) => {
    try {
      const res = await axios.post('accounts/v1/password-reset/request/', { phone_number });
      return { success: true, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Failed to send reset OTP.',
      };
    }
  };

  // ─── password reset ──────────────────────────────────────────────────────────
  // POST /api/accounts/v1/password-reset/reset/
  // Body: { phone_number, otp, new_password }
  const resetPassword = async (phone_number, otp, new_password) => {
    try {
      const res = await axios.post('accounts/v1/password-reset/reset/', {
        phone_number, otp, new_password,
      });
      return { success: true, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Password reset failed.',
      };
    }
  };

  // ─── update profile ──────────────────────────────────────────────────────────
  const updateProfile = async (data) => {
    try {
      const res = await authAxios().patch('accounts/profile/', data);
      const updated = { ...user, ...res.data };
      setUser(updated);
      localStorage.setItem('userData', JSON.stringify(updated));
      return { success: true, data: updated };
    } catch (err) {
      return { success: false, message: 'Failed to update profile.' };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    verifyPhone,
    resendOTP,
    requestPasswordReset,
    resetPassword,
    updateProfile,
    fetchUser,
    getToken,
    isAuthenticated: !!user,
    isClient:    user?.user_type === 'user',
    isAssistant: user?.user_type === 'assistant',
    isHandler:   user?.user_type === 'handler',
    isAdmin:     user?.user_type === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
